import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Chip,
    Box,
    Typography,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Checkbox,
    Paper, Grid
} from '@mui/material';
import { FaSearch, FaBox, FaTimes } from 'react-icons/fa';
import { getData } from '../../Webservice/Webservice';

const TabForm = ({ open, handleClose, handleSubmit, isEdit, editingTab, inline = false }) => {
    const [formData, setFormData] = useState({
        name: '',
        status: 'active',
        position: '',
        showTo: 'all',
        product_id: [],
    });
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState(['All']);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [showProductList, setShowProductList] = useState(false);
    const [loading, setLoading] = useState(false);

    // Fetch categories
    const fetchCategories = async () => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('categories/list?limit=1000', null, token);

            if (response && Array.isArray(response.categories)) {
                setCategories(response.categories);
                const categoryTitles = response.categories.map(cat => cat.title);
                setCategoryOptions(['All', ...categoryTitles]);
            } else {
                console.error('Invalid categories response:', response);
                setCategoryOptions(['All']);
            }
        } catch (error) {
            console.error('Fetch categories failed:', error);
            setCategoryOptions(['All']);
        }
    };

    // Fetch products
    const fetchProducts = async () => {
        try {
            setLoading(true);
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('product/list?isFree=false&limit=1000', null, token);

            const transformedProducts = Array.isArray(response.products)
                ? response.products.map(product => ({
                    id: product._id,
                    name: product.title,
                    category: product.category_id?.title || 'N/A',
                }))
                : [];
            setProducts(transformedProducts);
        } catch (error) {
            console.error('Error fetching products:', error);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    // Populate form with editingTab data if in edit mode
    useEffect(() => {
        if (isEdit && editingTab) {
            setFormData({
                name: editingTab.name || '',
                status: editingTab.status?.toLowerCase() || 'active',
                position: editingTab.position?.toString() || '',
                showTo: editingTab.showTo?.toLowerCase() || 'all',
                product_id: Array.isArray(editingTab.product_ids)
                    ? editingTab.product_ids
                    : (Array.isArray(editingTab.products) ? editingTab.products.map(p => p._id || p.id || p) : []),
            });
        } else {
            setFormData({
                name: '',
                status: 'active',
                position: '',
                showTo: 'all',
                product_id: [],
            });
        }
    }, [isEdit, editingTab, open]);

    // Fetch data when dialog opens
    useEffect(() => {
        if (open) {
            fetchCategories();
            fetchProducts();
            setShowProductList(true);
        }
    }, [open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleToggleProduct = (productId) => {
        const currentIndex = formData.product_id.indexOf(productId);
        const newChecked = [...formData.product_id];

        if (currentIndex === -1) {
            newChecked.push(productId);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setFormData((prev) => ({
            ...prev,
            product_id: newChecked,
        }));
    };

    const handleDeleteChip = (productIdToDelete) => {
        const newProductIds = formData.product_id.filter(id => id !== productIdToDelete);
        setFormData((prev) => ({
            ...prev,
            product_id: newProductIds,
        }));
    };

    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.id?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'All' ||
            product.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const handleFormSubmit = () => {
        handleSubmit(formData);
        handleClose();
    };

    const content = (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: inline ? 0 : 2.5,
                    px: inline ? 0 : 3,
                    backgroundColor: inline ? 'transparent' : '#fff',
                }}
            >
                <DialogTitle sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, fontSize: '1.5rem', py: inline ? 0 : 2.5, px: inline ? 0 : 3, color: 'black' }}>
                    {isEdit ? 'Edit Tab' : 'Add Tab'}
                </DialogTitle>
                <Button
                    onClick={handleClose}
                    sx={{
                        color: 'white',
                        backgroundColor: '#ef4444',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2,
                        '&:hover': {
                            borderColor: '#ef4444',
                            backgroundColor: '#ef4444',
                        },
                    }}
                    variant="contained"
                >
                    Back
                </Button>
            </Box>
            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleFormSubmit(); }}>
                <DialogContent >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Tab Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                    size="small"
                                    sx={{
                                        fontFamily: 'Mulish, sans-serif',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel sx={{ fontFamily: 'Mulish, sans-serif' }}>Status</InputLabel>
                                    <Select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        label="Status"
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            },
                                        }}
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                        </Grid>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Position"
                                    name="position"
                                    type="number"
                                    value={formData.position}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                    size="small"
                                    sx={{
                                        fontFamily: 'Mulish, sans-serif',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel sx={{ fontFamily: 'Mulish, sans-serif' }}>Show To</InputLabel>
                                    <Select
                                        name="showTo"
                                        value={formData.showTo.toLowerCase()}
                                        onChange={handleChange}
                                        label="Show To"
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            },
                                        }}
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="b2b">B2B</MenuItem>
                                        <MenuItem value="b2c">B2C</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: '#334155',
                                display: 'block',
                            }}
                        >
                            Product Information
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, }}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ fontFamily: 'Mulish, sans-serif' }}>Filter by Category</InputLabel>
                                <Select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    label="Filter by Category"
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                        },
                                    }}
                                >
                                    {categoryOptions.map((category) => (
                                        <MenuItem key={category} value={category}>
                                            {category}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onFocus={() => setShowProductList(true)}
                                fullWidth
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FaSearch style={{ color: '#64748b' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                    },
                                }}
                            />
                        </Box>
                        {formData.product_id.length > 0 && (
                            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {formData.product_id.map(productId => {
                                    const product = products.find(p => p.id === productId) || 
                                                    editingTab?.product_id?.find(p => (p._id || p.id) === productId);
                                    return (
                                        <Chip
                                            key={productId}
                                            label={product ? `${product.name || product.title} (${product.id || product._id})` : `ID: ${productId}`}
                                            onDelete={() => handleDeleteChip(productId)}
                                            deleteIcon={<FaTimes />}
                                            variant="outlined"
                                            sx={{
                                                backgroundColor: '#f0f4f8',
                                                borderRadius: '6px',
                                                '& .MuiChip-deleteIcon': {
                                                    color: '#64748b',
                                                    fontSize: '14px',
                                                    '&:hover': {
                                                        color: '#ef4444',
                                                    },
                                                },
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        )}
                        {showProductList && (
                            <Paper
                                elevation={2}
                                sx={{
                                    maxHeight: 200,
                                    overflow: 'auto',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0',
                                    mt: 1,
                                }}
                            >
                                <List dense>
                                    {loading ? (
                                        <ListItem>
                                            <ListItemText primary="Loading products..." />
                                        </ListItem>
                                    ) : filteredProducts.length > 0 ? (
                                        filteredProducts.map((product) => (
                                            <ListItem
                                                key={product.id}
                                                button
                                                onClick={() => handleToggleProduct(product.id)}
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: '#f1f5f9',
                                                    },
                                                }}
                                            >
                                                <ListItemIcon>
                                                    <Checkbox
                                                        edge="start"
                                                        checked={formData.product_id.indexOf(product.id) !== -1}
                                                        tabIndex={-1}
                                                        disableRipple
                                                        sx={{
                                                            color: 'rgb(255 209 2)',
                                                            '&.Mui-checked': {
                                                                color: 'rgb(255 209 2)',
                                                            },
                                                        }}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={product.name}
                                                    secondary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', }}>
                                                            <FaBox style={{ fontSize: 14, marginRight: 4, color: '#64748b' }} />
                                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                                {product.category}
                                                            </Typography>
                                                            <Chip
                                                                label={product.id}
                                                                size="small"
                                                                sx={{
                                                                    ml: 1,
                                                                    height: 20,
                                                                    fontSize: '0.7rem',
                                                                    backgroundColor: '#fff9e0ff',
                                                                    color: 'black',
                                                                }}
                                                            />
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                        ))
                                    ) : (
                                        <ListItem>
                                            <ListItemText primary="No products found" />
                                        </ListItem>
                                    )}
                                </List>
                            </Paper>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ paddingTop: '25px', paddingRight: '20px' }}>

                    <Button
                        type="submit"
                        variant="contained"
                        sx={{
                            fontFamily: 'Mulish, sans-serif',
                            backgroundColor: 'rgb(255 209 2)',
                            color: 'black',
                            border: '1px solid black',
                            fontWeight: 600,
                            borderRadius: '8px',
                            textTransform: 'none',
                            // px: 2,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            '&:hover': {
                                backgroundColor: 'rgb(255 209 2)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                            },
                        }}
                    >
                        {isEdit ? 'Update' : 'create'}
                    </Button>
                </DialogActions>
            </Box>
        </>
    );

    if (inline) {
        return (
            <Box sx={{ backgroundColor: '#fff', borderRadius: '12px', p: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
                {content}
            </Box>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                    overflow: 'hidden',
                },
            }}
        >
            {content}
        </Dialog>
    );
};

export default TabForm;